/**
 * Tipos de domínio do Projeto Viver.
 *
 * Escritos à mão de propósito: são o vocabulário do programa e precisam
 * sobreviver a uma regeneração de `src/tipos/supabase.ts` (que é gerado
 * por `pnpm db:types` e não deve ser editado).
 */

// ---------------------------------------------------------------------
// Enumerados — espelham os tipos criados na migration 01
// ---------------------------------------------------------------------

export const PILARES = [
  'alimentar',
  'desinflamar',
  'fortalecer',
  'descansar',
  'habitar',
] as const
export type Pilar = (typeof PILARES)[number]

export const QUALIDADES_SONO = ['excelente', 'bom', 'regular', 'ruim'] as const
export type QualidadeSono = (typeof QUALIDADES_SONO)[number]

export const NIVEIS_HUMOR = ['bem', 'neutro', 'mal'] as const
export type NivelHumor = (typeof NIVEIS_HUMOR)[number]

export const NIVEIS_FOME = ['baixa', 'normal', 'alta'] as const
export type NivelFome = (typeof NIVEIS_FOME)[number]

export const ESTADOS_INTESTINO = ['normal', 'preso', 'diarreia'] as const
export type EstadoIntestino = (typeof ESTADOS_INTESTINO)[number]

export const NIVEIS_INCHACO = ['nada', 'pouco', 'muito'] as const
export type NivelInchaco = (typeof NIVEIS_INCHACO)[number]

export const CATEGORIAS_VIDEO = [
  'reprogramacao_manha',
  'reprogramacao_noite',
  'cardio',
  'alongamento',
  'fortalecimento',
] as const
export type CategoriaVideo = (typeof CATEGORIAS_VIDEO)[number]

export const TIPOS_REFEICAO = ['cafe_manha', 'lanche_manha', 'almoco', 'lanche', 'jantar'] as const
export type TipoRefeicao = (typeof TIPOS_REFEICAO)[number]

export type TipoMissao = 'video' | 'pratica' | 'cardapio' | 'diario' | 'livre'
export type MomentoAvaliacao = 'inicial' | 'final'
export type TipoFoto = 'antes' | 'depois'
export type PapelUsuario = 'participante' | 'admin'
export type StatusMatricula = 'ativa' | 'concluida' | 'cancelada'

export const FASES_CICLO = ['menstrual', 'folicular', 'ovulatoria', 'lutea'] as const
export type FaseCiclo = (typeof FASES_CICLO)[number]

// ---------------------------------------------------------------------
// Rótulos para a interface
//
// Ficam junto do tipo para que adicionar um valor ao enum quebre o build
// aqui, e não silenciosamente na tela.
// ---------------------------------------------------------------------

export const ROTULO_PILAR: Record<Pilar, { nome: string; emoji: string }> = {
  alimentar: { nome: 'Alimentar', emoji: '🌿' },
  desinflamar: { nome: 'Desinflamar', emoji: '🍃' },
  fortalecer: { nome: 'Fortalecer', emoji: '💪' },
  descansar: { nome: 'Descansar', emoji: '🌙' },
  habitar: { nome: 'Habitar', emoji: '🌺' },
}

export const ROTULO_SONO: Record<QualidadeSono, string> = {
  excelente: 'Excelente',
  bom: 'Bom',
  regular: 'Regular',
  ruim: 'Ruim',
}

export const ROTULO_HUMOR: Record<NivelHumor, { rotulo: string; emoji: string }> = {
  bem: { rotulo: 'Bem', emoji: '😀' },
  neutro: { rotulo: 'Neutro', emoji: '😐' },
  mal: { rotulo: 'Mal', emoji: '😞' },
}

export const ROTULO_FOME: Record<NivelFome, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
}

export const ROTULO_INTESTINO: Record<EstadoIntestino, string> = {
  normal: 'Normal',
  preso: 'Preso',
  diarreia: 'Diarreia',
}

export const ROTULO_INCHACO: Record<NivelInchaco, string> = {
  nada: 'Nada',
  pouco: 'Pouco',
  muito: 'Muito',
}

export const ROTULO_REFEICAO: Record<TipoRefeicao, string> = {
  cafe_manha: 'Café da manhã',
  lanche_manha: 'Lanche da manhã',
  almoco: 'Almoço',
  lanche: 'Lanche da tarde',
  jantar: 'Jantar',
}

export const ROTULO_CATEGORIA_VIDEO: Record<CategoriaVideo, string> = {
  reprogramacao_manha: 'Reprogramação da manhã',
  reprogramacao_noite: 'Reprogramação da noite',
  cardio: 'Cardio',
  alongamento: 'Alongamento',
  fortalecimento: 'Fortalecimento muscular',
}

/**
 * Textos das fases do ciclo menstrual (seção 10.1).
 *
 * Informação educativa apenas. Nada aqui pode soar como recomendação
 * médica — é uma restrição explícita da especificação, e também o que
 * mantém o app fora do território de dispositivo de saúde.
 */
export const ROTULO_FASE_CICLO: Record<FaseCiclo, { nome: string; texto: string }> = {
  menstrual: {
    nome: 'Menstrual',
    texto: 'É comum sentir mais cansaço e vontade de desacelerar nesta fase.',
  },
  folicular: {
    nome: 'Folicular',
    texto: 'Costuma ser a fase de mais disposição e clareza mental.',
  },
  ovulatoria: {
    nome: 'Ovulatória',
    texto: 'Muitas mulheres relatam pico de energia e sociabilidade agora.',
  },
  lutea: {
    nome: 'Lútea',
    texto: 'É comum haver mais retenção de líquido e variação de humor.',
  },
}

// ---------------------------------------------------------------------
// Constantes do programa
// ---------------------------------------------------------------------

/** Fator da fórmula de água diária: peso (kg) × 40 = ml/dia (seção 3). */
export const FATOR_AGUA_ML_POR_KG = 40

/** Volume de referência de um copo, usado para converter a meta em copos. */
export const ML_POR_COPO = 250

/** Versão dos textos legais. Incrementar obriga novo aceite (LGPD). */
export const VERSAO_TERMOS = '2026-08-05'
export const VERSAO_CONSENTIMENTO_SAUDE = '2026-08-05'

/**
 * Quantidade de dígitos do código de acesso.
 *
 * PRECISA bater com o painel do Supabase, em
 * `Authentication › Sign In / Providers › Email › Email OTP Length`.
 *
 * Não é detalhe cosmético: um campo mais curto que o código gerado
 * trunca a entrada em silêncio, e a participante recebe "código
 * inválido" com o código correto em mãos. Foi exatamente o que
 * aconteceu com o valor anterior, 6, contra um Supabase gerando 8.
 */
export const TAMANHO_CODIGO_OTP = 8
